import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { InfoCircleOutlined } from "@ant-design/icons"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import TripDetailsCard from "./TripDetailsCard"
import PaymentMethod from "./PaymentMethod"
import PreviousPriceDetail from "./PreviousPriceDetail"
import styles from "./BookingForm.module.css"
import Stepper from "./Stepper"
import { useSelector, useDispatch } from "react-redux"
import PriceBreakdown from "./PriceBreakdown"
import { updateBookingSelections } from "store/experiences"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import RefundPayment from "./RefundPayment"
import { PAYMENT_OPTIONS } from "../constants"
import { numberToCurrency } from "helpers/string"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"

const { Title } = Typography

// Payment related calculations
const usePaymentCalculations = ({ booking, bookingSelections, depositPercent, customDepositAmount, t }) => {
  const { data } = useSelector((state) => state.bookings.refundDetails)
  const { non_refundable_amount = 0, cash_incomes, card_incomes } = data || {}
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash, full_cash } = PAYMENT_OPTIONS
  const hasCashPayment = booking?.payment_option === full_cash
  const hasDepositPayment = [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option)

  const { balanceAmount, refundDueNow } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount
  })

  const refundAlert = booking?.less_then_24h
    ? t("booking.non_refundable_24h", { amount: numberToCurrency(refundDueNow) })
    : t("booking.refund_alert", { amount: numberToCurrency(refundDueNow) })

  return {
    hasCashPayment,
    hasDepositPayment,
    balanceAmount,
    refundDueNow,
    refundAlert,
    non_refundable_amount,
    cash_incomes,
    card_incomes
  }
}

// Price comparison logic
const usePriceComparison = (booking, bookingSelections) => {
  return useMemo(
    () => ({
      isChanged: Number(booking?.booked_amount) !== Number(bookingSelections?.amount),
      isDecreased: Number(booking?.booked_amount) > Number(bookingSelections?.amount)
    }),
    [booking?.booked_amount, bookingSelections?.amount]
  )
}

const EditBookingForm = ({ form, bookingUuid, bookAgain, booking, onSubmit, onCancelModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [alertMessage, setAlertMessage] = useState("")
  const [currentStep, setCurrentStep] = useState(1)

  const {
    experienceById: { experienceDetail },
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  const clientName = useWatch("existing_client", form)
  const clientFirstName = useWatch("firstName", form)
  const clientLastName = useWatch("lastName", form)
  const clientEmail = useWatch("email", form)
  const clientPhone = useWatch("phone_number", form)
  const depositPercent = useWatch("depositPercent", form) || booking?.deposit_percentage || ""
  const customDepositAmount = useWatch("customDepositAmount", form)
  const isClientDetailsFilled = !!(((clientFirstName && clientLastName) || clientName) && clientEmail && clientPhone) && !alertMessage

  const { hasCashPayment, hasDepositPayment, balanceAmount, refundDueNow, refundAlert, non_refundable_amount, cash_incomes, card_incomes } =
    usePaymentCalculations({
      booking,
      bookingSelections,
      depositPercent,
      customDepositAmount,
      t
    })

  const { isChanged: isPriceChanged, isDecreased: isPriceDecreased } = usePriceComparison(booking, bookingSelections)

  // Navigation state
  const isStep1 = currentStep === 1
  const isStep2 = currentStep === 2

  // Derived state
  const priceDecreasedNoRefund = isStep2 && isPriceDecreased && refundDueNow === 0 && balanceAmount === 0
  const depositDecreasedNoRefund = isStep2 && isPriceDecreased && hasDepositPayment

  // Navigation handlers
  const goToStep2 = async () => {
    try {
      await form.validateFields()
      setCurrentStep(2)
    } catch (error) {
      console.error("Form validation failed:", error)
    }
  }

  const goToStep1 = () => setCurrentStep(1)

  // Booking total calculation and effect
  const { total, breakdown } = useMemo(
    () =>
      calculateBookingTotal(
        bookingSelections,
        bookingSelections?.counts,
        experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      ) || { total: 0, breakdown: [] },
    [bookingSelections, experienceDetail?.guide_booking_fee_percentage, booking?.booking_fee_percentage]
  )

  // Update Redux when total changes
  useEffect(() => {
    if (total !== bookingSelections?.amount) {
      dispatch(updateBookingSelections({ amount: total, breakdown }))
    }
  }, [total, breakdown, bookingSelections?.amount, dispatch])

  // Render step 1 content
  const renderStep1 = () => (
    <>
      <Card className={styles.cardContainer}>
        <Title className={styles.cardTitle} level={5}>
          {t("booking.client_detail")}
        </Title>
        <ClientDetail form={form} bookingId={bookingUuid} alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      </Card>
      <Card className={styles.cardContainer}>
        <Title className={styles.cardTitle} level={5}>
          {t("booking.experience_details")}
        </Title>
        <ExperienceDetail form={form} bookingId={bookingUuid} booking={booking} isEdit={true} bookAgain={bookAgain} />
      </Card>
    </>
  )

  // Render step 2 content
  const renderStep2 = () => (
    <>
      <div className={styles.tripDetailsCard}>
        <Title level={5} className={styles.tripDetailsTitle}>
          {t("booking.trip_details")}
        </Title>
        <TripDetailsCard booking={booking} bookingSelections={bookingSelections} />
      </div>

      {!isPriceChanged && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.trip_price_not_changed")}</span>
        </div>
      )}
    </>
  )

  // Render price comparison
  const renderPriceComparison = () => {
    if (!isPriceChanged) {
      return (
        <Card className={`${styles.cardContainer} ${styles.column}`}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.price_breakdown")}
          </Title>
          <PreviousPriceDetail form={form} booking={booking} isEdit={true} />
        </Card>
      )
    }

    return (
      <div className={styles.comparisonContainer}>
        <Card className={styles.comparisonCard}>
          <Title className={styles.comparisonTitle} level={5}>
            {t("booking.previous_price_details")}
          </Title>
          <PreviousPriceDetail form={form} booking={booking} isEdit={true} />
        </Card>
        <Card className={styles.comparisonCard}>
          <Title className={styles.comparisonTitle} level={5}>
            {t("booking.updated_price_details")}
          </Title>
          <PriceBreakdown
            form={form}
            booking={booking}
            isEdit={true}
            isPriceDecreased={isPriceDecreased}
            hasDepositPayment={hasDepositPayment}
            non_refundable_amount={non_refundable_amount}
          />
        </Card>
      </div>
    )
  }

  // Render refund section
  const renderRefundSection = () => (
    <div style={{ display: isStep2 && refundDueNow > 0 ? "block" : "none" }}>
      <Card className={styles.cardContainer}>
        <Title className={styles.cardTitle} level={5}>
          {t("booking.refund_payment")}
        </Title>
        <div className={styles.infoNotice} style={{ display: refundAlert && !hasCashPayment ? "block" : "none" }}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{refundAlert}</span>
        </div>
        <div style={{ display: hasCashPayment || booking?.less_then_24h ? "block" : "none" }}>
          <RefundPayment
            form={form}
            refundDueNow={refundDueNow}
            isStep2={isStep2}
            booking={booking}
            bookingUuid={bookingUuid}
            cash_incomes={cash_incomes}
            card_incomes={card_incomes}
          />
        </div>
      </Card>
    </div>
  )

  // Render price decrease notices
  const renderPriceDecreaseNotices = () => (
    <>
      {priceDecreasedNoRefund && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.price_decreased_no_refund")}</span>
        </div>
      )}
      {depositDecreasedNoRefund && !refundDueNow && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.price_decreased")}</span>
        </div>
      )}
    </>
  )

  // Render payment method section
  const renderPaymentMethod = () =>
    isStep2 &&
    isPriceChanged &&
    refundDueNow <= 0 &&
    !priceDecreasedNoRefund && (
      <div className={styles.twoColumnLayout} style={{ display: depositDecreasedNoRefund ? "none" : "block" }}>
        <Card className={`${styles.cardContainer} ${styles.column}`}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.payment_method")}
          </Title>
          <PaymentMethod
            form={form}
            bookingId={bookingUuid}
            booking={booking}
            isEdit={true}
            bookAgain={bookAgain}
            isPriceChanged={isPriceChanged}
          />
        </Card>
      </div>
    )

  // Render form actions
  const renderFormActions = () => (
    <Form.Item className={styles.formActions}>
      <Space>
        {isStep1 ? (
          <>
            <Button variant="outlined" color="primary" htmlType="button" onClick={onCancelModal}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" htmlType="button" onClick={goToStep2} disabled={!isClientDetailsFilled}>
              {t("global.next")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" color="primary" htmlType="button" onClick={goToStep1}>
              {t("global.back_to_previous_step")}
            </Button>
            <Button type="primary" htmlType="button" onClick={onSubmit}>
              {bookingUuid && !bookAgain ? t("global.update_trip") : t("global.submit")}
            </Button>
          </>
        )}
      </Space>
    </Form.Item>
  )

  return (
    <CustomForm layout="vertical" form={form} onFinish={onSubmit}>
      <Stepper activeStep={currentStep} t={t} />
      <div className={styles.scrollContainer}>
        {isStep1 ? (
          renderStep1()
        ) : (
          <>
            {renderStep2()}
            {renderPriceComparison()}
            {renderRefundSection()}
            {renderPriceDecreaseNotices()}
            {renderPaymentMethod()}
          </>
        )}
      </div>
      {renderFormActions()}
    </CustomForm>
  )
}

EditBookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired
}

export default EditBookingForm
